/**
 * useUnifiedCapabilities Hook
 * ============================
 * Fetches installed apps + connected integrations from the unified registry.
 * Returns them as NodePalette-compatible categories.
 */

import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../../../config';

let unifiedCache = null;
let unifiedCacheTimestamp = null;
const CACHE_TTL = 60 * 1000; // 1 minute

export const useUnifiedCapabilities = () => {
  const [installedApps, setInstalledApps] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUnified = useCallback(async (force = false) => {
    if (!force && unifiedCache && unifiedCacheTimestamp && (Date.now() - unifiedCacheTimestamp < CACHE_TTL)) {
      setInstalledApps(unifiedCache.installed_apps || []);
      setIntegrations(unifiedCache.integrations || []);
      setSummary(unifiedCache.summary || null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/unified-capabilities/registry`, {
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        unifiedCache = data;
        unifiedCacheTimestamp = Date.now();
        setInstalledApps(data.installed_apps || []);
        setIntegrations(data.integrations || []);
        setSummary(data.summary || null);
        setError(null);
      } else {
        throw new Error('Failed to fetch unified capabilities');
      }
    } catch (err) {
      console.error('Error fetching unified capabilities:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnified();
  }, [fetchUnified]);

  const refresh = useCallback(() => fetchUnified(true), [fetchUnified]);

  return { installedApps, integrations, summary, loading, error, refresh };
};

export const clearUnifiedCache = () => {
  unifiedCache = null;
  unifiedCacheTimestamp = null;
};
